import { Component, input } from '@angular/core';
import { Params, RouterLink } from '@angular/router';
import type { LucideIconInput } from '@lucide/angular';
import { AdminCardComponent } from './admin-card.component';
import { AdminIconComponent } from './admin-icon.component';

export type AdminStatCardTint = 'blue' | 'amber' | 'violet' | 'green' | 'slate';

@Component({
  selector: 'app-admin-stat-card',
  standalone: true,
  imports: [AdminCardComponent, AdminIconComponent, RouterLink],
  template: `
    @if (routerLink(); as link) {
      <a class="admin-stat-card__link" [routerLink]="link" [queryParams]="queryParams()">
        <app-admin-card class="admin-stat-card">
          <span class="admin-stat-card__icon admin-stat-card__icon--{{ tint() }}">
            <app-admin-icon [name]="icon()" />
          </span>
          <p class="admin-stat-card__label">{{ title() }}</p>
          <p class="admin-stat-card__value">{{ value() }}</p>
          @if (subtext()) {
            <p class="admin-stat-card__subtext">{{ subtext() }}</p>
          }
        </app-admin-card>
      </a>
    } @else {
      <app-admin-card class="admin-stat-card">
        <span class="admin-stat-card__icon admin-stat-card__icon--{{ tint() }}">
          <app-admin-icon [name]="icon()" />
        </span>
        <p class="admin-stat-card__label">{{ title() }}</p>
        <p class="admin-stat-card__value">{{ value() }}</p>
        @if (subtext()) {
          <p class="admin-stat-card__subtext">{{ subtext() }}</p>
        }
      </app-admin-card>
    }
  `,
  styles: `
    :host {
      display: block;
      min-width: 0;
    }

    .admin-stat-card__link {
      display: block;
      color: inherit;
      text-decoration: none;
    }

    .admin-stat-card__link:hover ::ng-deep .admin-stat-card .cg-admin-card {
      border-color: #c7d2fe;
      box-shadow: 0 8px 20px rgba(79, 70, 229, 0.08);
    }

    .admin-stat-card {
      text-align: center;
    }

    :host ::ng-deep .admin-stat-card .cg-admin-card {
      min-height: 10.5rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 1.25rem 1rem;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .admin-stat-card__icon {
      width: 2.5rem;
      height: 2.5rem;
      border-radius: 999px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 0.5rem;
    }

    .admin-stat-card__icon app-admin-icon {
      display: inline-flex;
      color: inherit;
    }

    .admin-stat-card__icon--blue {
      background: #e8eefc;
      color: #334155;
    }

    .admin-stat-card__icon--amber {
      background: #fff6dc;
      color: #b45309;
    }

    .admin-stat-card__icon--violet {
      background: #f1e8ff;
      color: #7c3aed;
    }

    .admin-stat-card__icon--green {
      background: #dcfce7;
      color: #15803d;
    }

    .admin-stat-card__icon--slate {
      background: #f1f5f9;
      color: #475569;
    }

    .admin-stat-card__label {
      margin: 0;
      text-transform: uppercase;
      color: #6b7280;
      font-weight: 700;
      letter-spacing: 0.04em;
      font-size: 0.72rem;
    }

    .admin-stat-card__value {
      margin: 0.35rem 0 0;
      font-size: 2rem;
      line-height: 1.1;
      font-weight: 700;
      color: #0f172a;
    }

    .admin-stat-card__subtext {
      margin: 0.35rem 0 0;
      color: #6b7280;
      font-size: 0.8rem;
    }
  `,
})
export class AdminStatCardComponent {
  readonly title = input.required<string>();
  readonly value = input.required<string | number>();
  readonly subtext = input<string>();
  readonly icon = input.required<LucideIconInput>();
  readonly tint = input<AdminStatCardTint>('blue');
  readonly routerLink = input<string | string[]>();
  readonly queryParams = input<Params>();
}
